import {
  ResumeSectionType,
  ContactInfo,
  ContactExtraLink,
  WorkExperienceCompany,
} from '@applicator/shared'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FileFormatDocxIcon } from './icons/FileFormatDocxIcon'
import { FileFormatPdfIcon } from './icons/FileFormatPdfIcon'

import {
  flushPendingResumeSectionSaves,
  type PendingSectionSave,
  type PendingSectionSaveRef,
  scheduleDebouncedResumeSectionSave,
} from '../utils/resumeSectionSaveScheduler'
import {
  fetchJdPrecheck,
  JdPrecheckBlockedError,
} from '../lib/optimization/optimizationJdPrecheck'
import { JD_PRECHECK_NETWORK_FALLBACK } from '../lib/optimization/jdPrecheckMessages'
import { useResumeOptimization } from '../hooks/useResumeOptimization'
import { supabase } from '../lib/supabase'
import JobDescriptionModal from './ResumeDetail/optimization/JobDescriptionModal'
import OptimizationQualityGate from './ResumeDetail/optimization/OptimizationQualityGate'
import OptimizationBlockOverlay from './ResumeDetail/optimization/OptimizationBlockOverlay'
import OptimizationBlockBadge from './ResumeDetail/optimization/OptimizationBlockBadge'
import OptimizationAgentPanel from './ResumeDetail/optimization/OptimizationAgentPanel'
import OptimizationContactRecommendations from './ResumeDetail/optimization/OptimizationContactRecommendations'
import OptimizationSkillsGapRecommendations from './ResumeDetail/optimization/OptimizationSkillsGapRecommendations'
import OptimizationFailureDialog from './ResumeDetail/optimization/OptimizationFailureDialog'
import QuotaExhaustedModal from './billing/QuotaExhaustedModal'
import ResumeOptimizeButton from './ResumeDetail/optimization/ResumeOptimizeButton'
import { Button } from './Button'
import { InstantTooltip } from './InstantTooltip'
import type { OptimizationBlockState } from '../lib/optimization/types'
import { blockIdForSection, hasWorkExperienceBullets } from '../lib/optimization/blockIds'
import {
  buildOptimizationAgentUiState,
  isSectionQualityFlagged,
  isSectionDimmedForOptimization,
  isSectionFocusedForOptimization,
} from '../lib/optimization/optimizationAgentUi'

import {
  collectQualityIssues,
  collectClarifyingQuestions,
  collectSectionReviewIssues,
  extractedTargetTitleFromRun,
  issuesForSection,
  targetTitleAlignedWithExtracted,
} from '../lib/optimization/qualityEvalFlagging'
import {
  feedbackProgressContextFromResumeData,
  filterContactRecommendationsByProgress,
  filterQualityIssuesByProgress,
  filterSkillGapSummary,
  summaryPassesWritingChecks,
  type FeedbackProgressContext,
} from '../lib/optimization/qualityFeedbackProgress'
import {
  extractSkillGapSummary,
  skillGapHasActionItems,
} from '../lib/optimization/skillGapRecommendations'
import SectionQualityFeedback from './ResumeDetail/optimization/SectionQualityFeedback'
import { SECTION_FEEDBACK_STACK_CLASS } from './ResumeDetail/optimization/sectionFeedbackCallout'
import { regenerateProfessionalSummary } from '../lib/optimization/regenerateSummary'
import { showError as showToastError, showSuccess as showToastSuccess } from '../lib/toast'
import { trackResumeExported } from '../lib/posthogEvents'
import { exportResumeToPDF, exportResumeToDOCX } from '../utils/resumeExport'
import {
  resumeDetailNaturalPreviewWidthPx,
  useResumeDetailSplitLayout,
} from '../utils/resumeDetailSplitLayout'
import { useResumePreviewOnlyMode } from '../utils/useResumePreviewOnlyMode'
import { useResumePreviewDebugModes } from '../utils/useResumePreviewDebugModes'
import {
  loadResumeTemplatePreference,
  saveResumeTemplatePreference,
  type ResumeTemplateId,
} from '../types/resumeTemplate'
import {
  loadResumeColorPreference,
  resolveResumeColors,
  saveResumeColorPreference,
  type ResumeColorPreference,
} from '../types/resumeColorScheme'

import {
  sanitizeWorkExperienceCompanies,
  type WorkExperienceGroupMode,
} from '../utils/workExperienceGroup'
import {
  applyWorkExperienceDisplayOrder,
  cloneWorkExperienceCompanies,
  normalizeWorkExperienceForPositionMode,
  type WorkExperienceSortMode,
} from '../utils/workExperienceSort'
import {
  loadWorkExperienceSortState,
  resolveWorkExperienceManualBackup,
  saveWorkExperienceSortState,
} from '../utils/workExperienceSortStorage'

import { RESUME_SECTIONS } from './ResumeDetail/constants'
import { handleSectionAddClick, updateContactInfo as updateContactInfoHandler } from './ResumeDetail/handlers'
import { AlertTriangle } from 'lucide-react'
import {
  qualityFlaggedSectionsExpandKey,
  qualityFlaggedSectionsNewlyAdded,
  scrollToSelectorInLeftPanel,
  toggleSection,
} from './ResumeDetail/helpers'
import ResumePreview from './ResumeDetail/ResumePreview'
import ResumePreviewPageGuides from './ResumeDetail/ResumePreviewPageGuides'
import ResumePreviewSettingsRail from './ResumeDetail/ResumePreviewSettingsRail'
import ResumeSectionEditor from './ResumeDetail/ResumeSectionEditor'
import ResumeDetailSplitResizeHandle from './ResumeDetail/ResumeDetailSplitResizeHandle'
import WorkExperienceSectionMenu from './ResumeDetail/WorkExperienceSectionMenu'
import { useResumeData } from './ResumeDetail/useResumeData'
import { useResumeDetailEditorResize } from '../utils/useResumeDetailEditorResize'
import { useAccountProfessionalSummaryLibrary } from '../hooks/useAccountProfessionalSummaryLibrary'
import { useAccountTargetTitleLibrary } from '../hooks/useAccountTargetTitleLibrary'
import TemplateResumeMarkIcon from './TemplateResumeMarkIcon'
import {
  fetchTemplateResumes,
  fetchTemplateSkills,
  setResumeTemplateFlag,
  type TemplateResumeSummary,
} from '../lib/resumeTemplates'

const HAS_SCROLL_CLASS = 'has-scroll'

function ResumeDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    resume,
    loading,
    error,
    resumeData,
    setResume,
    setResumeData,
    loadResume,
    saveResumeContent,
    getActiveResumeId,
  } = useResumeData(id)
  const {
    variants: accountSummaryVariants,
    loading: accountSummaryVariantsLoading,
  } = useAccountProfessionalSummaryLibrary(id)
  const {
    variants: accountTargetTitleVariants,
    loading: accountTargetTitleVariantsLoading,
  } = useAccountTargetTitleLibrary(id)
  const [expandedSections, setExpandedSections] = useState<Set<ResumeSectionType>>(new Set())
  const [highlightedRoleId, setHighlightedRoleId] = useState<string | null>(null)
  const [highlightedBulletId, setHighlightedBulletId] = useState<string | null>(null)
  const [highlightedCompanyId, setHighlightedCompanyId] = useState<string | null>(null)
  const [highlightedTargetTitleId, setHighlightedTargetTitleId] = useState<string | null>(null)
  const [highlightedSummaryId, setHighlightedSummaryId] = useState<string | null>(null)
  const [activeSkillCategoryName, setActiveSkillCategoryName] = useState<string | null>(null)
  const [editingResumeTitle, setEditingResumeTitle] = useState(false)
  const [resumeTitleValue, setResumeTitleValue] = useState('')
  const [templateResumes, setTemplateResumes] = useState<TemplateResumeSummary[]>([])
  const [togglingTemplate, setTogglingTemplate] = useState(false)
  const saveTimeoutRef = useRef<Record<ResumeSectionType, NodeJS.Timeout | null>>({
    contact_info: null,
    target_title: null,
    professional_summary: null,
    work_experience: null,
    education: null,
    skills: null,
    interests: null,
    certifications: null,
    awards_scholarships: null,
    projects: null,
  })
  const pendingSectionSaveRef = useRef<Partial<Record<ResumeSectionType, PendingSectionSave>>>({})
  const addSummaryEntryRef = useRef<(() => void) | null>(null)
  const addWorkExperienceRef = useRef<(() => void) | null>(null)
  const addEducationRef = useRef<(() => void) | null>(null)
  const addRoleRef = useRef<(() => void) | null>(null)
  const addTargetTitleRef = useRef<(() => void) | null>(null)
  const addContactExtraFieldRef = useRef<(() => void) | null>(null)
  const addCertificationRef = useRef<(() => void) | null>(null)
  const addProjectRef = useRef<(() => void) | null>(null)
  const addInterestRef = useRef<(() => void) | null>(null)
  const splitRef = useRef<HTMLDivElement>(null)
  const leftPanelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!expandedSections.has('skills')) {
      setActiveSkillCategoryName(null)
    }
  }, [expandedSections])

  useEffect(() => {
    let cancelled = false
    void fetchTemplateResumes(supabase)
      .then((templates) => {
        if (!cancelled) setTemplateResumes(templates)
      })
      .catch((err) => {
        console.error('Failed to load template resumes:', err)
      })
    return () => {
      cancelled = true
    }
  }, [resume?.isTemplate, resume?.id])
  const rightPanelRef = useRef<HTMLDivElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const naturalPreviewWidthPx = useState(() => resumeDetailNaturalPreviewWidthPx())[0]
  const previewOnly = useResumePreviewOnlyMode()
  const { lineGuides, fontSizeDebug } = useResumePreviewDebugModes()
  const { previewPaddingPx } = useResumeDetailSplitLayout(splitRef, naturalPreviewWidthPx)
  const {
    editorPanelStyle,
    isResizing,
    onResizePointerDown,
    isResized,
  } = useResumeDetailEditorResize(splitRef, previewOnly)
  const effectivePreviewPaddingPx = previewOnly ? 20 : previewPaddingPx
  const [resumeTemplate, setResumeTemplate] = useState<ResumeTemplateId>(loadResumeTemplatePreference)
  const [resumeColorPreference, setResumeColorPreference] = useState<ResumeColorPreference>(loadResumeColorPreference)
  const resumeColors = useMemo(
    () => resolveResumeColors(resumeColorPreference),
    [resumeColorPreference]
  )
  const handleResumeColorPreferenceChange = useCallback((pref: ResumeColorPreference) => {
    setResumeColorPreference(pref)
    saveResumeColorPreference(pref)
  }, [])

  const handleToggleTemplateResume = useCallback(async () => {
    if (!resume || !id) return
    const next = !resume.isTemplate
    try {
      setTogglingTemplate(true)
      await setResumeTemplateFlag(supabase, id, next)
      setResume((prev) => (prev ? { ...prev, isTemplate: next } : prev))
      const templates = await fetchTemplateResumes(supabase)
      setTemplateResumes(templates)
      showToastSuccess(next ? 'Marked as template resume' : 'Removed template mark')
    } catch (err) {
      console.error('Failed to toggle template resume:', err)
      showToastError(err instanceof Error ? err.message : 'Failed to update template flag')
    } finally {
      setTogglingTemplate(false)
    }
  }, [id, resume, setResume])

  const fetchTemplateSkillsForResume = useCallback(
    (templateResumeId: string) => fetchTemplateSkills(supabase, templateResumeId),
    [],
  )
  const [workExperienceSortMode, setWorkExperienceSortMode] =
    useState<WorkExperienceSortMode>('manual')
  const [workExperienceGroupMode, setWorkExperienceGroupMode] =
    useState<WorkExperienceGroupMode>('company')
  const workExperienceManualBackupRef = useRef<WorkExperienceCompany[] | null>(null)
  const workExperienceSortHydratedRef = useRef(false)
  const [jdDraft, setJdDraft] = useState('')
  const [summaryRegenerating, setSummaryRegenerating] = useState(false)
  const [eligibilityWarnings, setEligibilityWarnings] = useState<
    Array<{ code: string; title: string; detail: string }>
  >([])
  const [eligibilityLoading, setEligibilityLoading] = useState(false)
  const [eligibilityReady, setEligibilityReady] = useState(false)
  const [jdPrecheckLoading, setJdPrecheckLoading] = useState(false)
  const [jdSecurityBlocked, setJdSecurityBlocked] = useState(false)
  const [jdSecurityMessage, setJdSecurityMessage] = useState<string | null>(null)
  const [jdSecurityMediumMessage, setJdSecurityMediumMessage] = useState<string | null>(null)
  const [jdPrecheckNetworkWarning, setJdPrecheckNetworkWarning] = useState<string | null>(null)

  const reloadResumeSilently = useCallback(() => {
    void loadResume({ silent: true })
  }, [loadResume])

  const optimization = useResumeOptimization({
    resumeId: id,
    workExperience: resumeData.work_experience,
    onBlocksProgress: reloadResumeSilently,
    onRunCompleted: reloadResumeSilently,
  })

  const workExperienceOptimizationBlock = useMemo((): OptimizationBlockState | undefined => {
    const wxBlocks = (optimization.activeRun?.blocks || []).filter((b) => b.company_name)
    if (!wxBlocks.length) {
      return optimization.getBlockForSection('work_experience')
    }
    const running = wxBlocks.find((b) => b.status === 'running')
    if (running) return running
    const failed = wxBlocks.find((b) => b.status === 'failed')
    if (failed) return failed
    if (wxBlocks.every((b) => b.status === 'done' || b.status === 'skipped')) {
      const duration_ms = wxBlocks.reduce((sum, b) => sum + (b.duration_ms || 0), 0)
      return { ...wxBlocks[0], label: 'Work Experience', duration_ms }
    }
    return wxBlocks.find((b) => b.status !== 'pending')
  }, [optimization.activeRun?.blocks, optimization.getBlockForSection])

  const resolveSectionOptimizationBlock = useCallback(
    (sectionType: ResumeSectionType, companyName?: string): OptimizationBlockState | undefined => {
      if (sectionType === 'work_experience') {
        if (companyName) {
          return optimization.getBlockForSection('work_experience', companyName)
        }
        return workExperienceOptimizationBlock
      }
      const blockId = blockIdForSection(sectionType)
      if (!blockId) return undefined
      return optimization.getBlockById(blockId)
    },
    [optimization.getBlockById, optimization.getBlockForSection, workExperienceOptimizationBlock]
  )

  const optimizationHasWxBullets = useMemo(
    () => hasWorkExperienceBullets(resumeData.work_experience),
    [resumeData.work_experience]
  )

  const optimizationQualityIssues = useMemo(
    () =>
      collectQualityIssues(
        optimization.qualityEval,
        optimization.activeRun?.feedback_json as Record<string, unknown> | undefined
      ),
    [optimization.qualityEval, optimization.activeRun?.feedback_json]
  )

  const optimizationClarifyingQuestions = useMemo(
    () =>
      collectClarifyingQuestions(
        optimization.activeRun?.feedback_json as Record<string, unknown> | undefined
      ),
    [optimization.activeRun?.feedback_json]
  )

  const extractedTargetTitle = useMemo(
    () =>
      extractedTargetTitleFromRun(
        optimization.activeRun?.feedback_json as Record<string, unknown> | undefined,
        optimization.activeRun?.blocks
      ),
    [optimization.activeRun?.feedback_json, optimization.activeRun?.blocks]
  )

  const targetTitleAligned = useMemo(
    () => targetTitleAlignedWithExtracted(resumeData.target_title, extractedTargetTitle),
    [resumeData.target_title, extractedTargetTitle]
  )

  const wxSelfHealApplied = Boolean(
    (optimization.activeRun?.feedback_json as { work_experience_self_heal?: { applied?: boolean } } | undefined)
      ?.work_experience_self_heal?.applied
  )

  const optimizationSkillGapSummaryRaw = useMemo(
    () => extractSkillGapSummary(optimization.activeRun),
    [optimization.activeRun]
  )

  const sectionReviewIssues = useMemo(
    () =>
      collectSectionReviewIssues(
        optimization.activeRun?.feedback_json as Record<string, unknown> | undefined,
        {
          suppressTargetTitleReview: targetTitleAligned,
          suppressSkillsSectionReview: skillGapHasActionItems(optimizationSkillGapSummaryRaw),
          suppressWorkExperienceSectionReview: wxSelfHealApplied,
        }
      ),
    [
      optimization.activeRun?.feedback_json,
      targetTitleAligned,
      optimizationSkillGapSummaryRaw,
      wxSelfHealApplied,
    ]
  )

  const sectionReviewIssuesForUi = sectionReviewIssues

  const feedbackProgressCtx = useMemo<FeedbackProgressContext>(
    () => ({
      ...feedbackProgressContextFromResumeData(resumeData),
      jobDescription: optimization.activeRun?.job_description ?? undefined,
    }),
    [resumeData, optimization.activeRun?.job_description]
  )

  const activeQualityIssues = useMemo(
    () =>
      filterQualityIssuesByProgress(
        [...optimizationQualityIssues, ...optimizationClarifyingQuestions, ...sectionReviewIssuesForUi],
        feedbackProgressCtx
      ),
    [
      optimizationQualityIssues,
      optimizationClarifyingQuestions,
      sectionReviewIssuesForUi,
      feedbackProgressCtx,
    ]
  )

  useEffect(() => {
    if (!optimization.jdModalOpen) {
      setEligibilityWarnings([])
      setEligibilityLoading(false)
      setEligibilityReady(false)
      setJdPrecheckLoading(false)
      setJdSecurityBlocked(false)
      setJdSecurityMessage(null)
      setJdSecurityMediumMessage(null)
      setJdPrecheckNetworkWarning(null)
      return undefined
    }
    const jd = jdDraft.trim()
    if (jd.length < 80) {
      setEligibilityWarnings([])
      setEligibilityLoading(false)
      setEligibilityReady(false)
      setJdPrecheckLoading(false)
      setJdSecurityBlocked(false)
      setJdSecurityMessage(null)
      setJdSecurityMediumMessage(null)
      setJdPrecheckNetworkWarning(null)
      return undefined
    }
    let cancelled = false
    setEligibilityWarnings([])
    setEligibilityLoading(false)
    setEligibilityReady(false)
    setJdPrecheckLoading(true)
    setJdSecurityBlocked(false)
    setJdSecurityMessage(null)
    setJdSecurityMediumMessage(null)
    setJdPrecheckNetworkWarning(null)
    const timer = setTimeout(() => {
      void (async () => {
        let blocked = false
        try {
          const { data: sessionData } = await supabase.auth.getSession()
          const token = sessionData.session?.access_token
          if (cancelled) return
          if (!token) {
            setJdPrecheckLoading(false)
            setEligibilityReady(true)
            return
          }

          const sanitizedJd = jd
          try {
            const precheck = await fetchJdPrecheck({
              jobDescription: jd,
              accessToken: token,
            })
            if (cancelled) return
            if (precheck.risk_tier === 'medium' && precheck.user_message) {
              setJdSecurityMediumMessage(precheck.user_message)
            }
          } catch (precheckError) {
            if (cancelled) return
            if (precheckError instanceof JdPrecheckBlockedError) {
              blocked = true
              setJdSecurityBlocked(true)
              setJdSecurityMessage(precheckError.message)
              return
            }
            setJdPrecheckNetworkWarning(JD_PRECHECK_NETWORK_FALLBACK)
          } finally {
            if (!cancelled) setJdPrecheckLoading(false)
          }

          if (blocked || cancelled) return

          setEligibilityLoading(true)
          const contact = resumeData.contact_info as ContactInfo | null
          const profileLocation =
            (contact?.location && String(contact.location).trim()) || 'Lisbon'
          const result = await fetchOptimizationEligibility({
            jobDescription: sanitizedJd,
            profileLocation,
            accessToken: token,
          })
          if (!cancelled) {
            setEligibilityWarnings(
              result.warnings.map((w) => ({
                code: w.code,
                title: w.title,
                detail: w.detail,
              }))
            )
            if (result.jd_security_message && result.jd_risk_tier === 'medium') {
              setJdSecurityMediumMessage(result.jd_security_message)
            }
          }
        } catch {
          if (!cancelled) setEligibilityWarnings([])
        } finally {
          if (!cancelled) {
            setEligibilityLoading(false)
            if (!blocked) setEligibilityReady(true)
          }
        }
      })()
    }, 600)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [jdDraft, optimization.jdModalOpen, resumeData.contact_info])

  const optimizationSkillGapSummary = useMemo(
    () => filterSkillGapSummary(optimizationSkillGapSummaryRaw, resumeData.skills ?? []),
    [optimizationSkillGapSummaryRaw, resumeData.skills]
  )

  const qualityFlaggedSectionsKey = useMemo(
    () => qualityFlaggedSectionsExpandKey(activeQualityIssues, optimizationSkillGapSummary),
    [activeQualityIssues, optimizationSkillGapSummary]
  )
  const qualityFlaggedSectionsKeyRef = useRef('')

  useEffect(() => {
    const previousKey = qualityFlaggedSectionsKeyRef.current
    qualityFlaggedSectionsKeyRef.current = qualityFlaggedSectionsKey
    if (!qualityFlaggedSectionsKey) return

    const toExpand = qualityFlaggedSectionsNewlyAdded(previousKey, qualityFlaggedSectionsKey)
    if (!toExpand.length) return

    setExpandedSections((prev) => {
      const next = new Set(prev)
      for (const sectionType of toExpand) next.add(sectionType)
      return next
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- expand only when a new flagged section appears
  }, [qualityFlaggedSectionsKey])

  const optimizationAgentUi = useMemo(
    () =>
      buildOptimizationAgentUiState({
        starting: optimization.starting,
        activeRun: optimization.runForAgentUi,
        runUiStartedAtMs: optimization.runUiStartedAtMs,
        nowMs: Date.now(),
        hasWorkExperienceBullets: optimizationHasWxBullets,
        qualityIssues: activeQualityIssues,
      }),
    [
      optimization.starting,
      optimization.runForAgentUi,
      optimization.runUiStartedAtMs,
      optimization.agentUiTick,
      optimizationHasWxBullets,
      activeQualityIssues,
    ]
  )

  const tailoringAgentSidePanelOpen =
    optimizationAgentUi.showPanel &&
    !optimization.agentPanelMinimized &&
    !optimization.agentPanelDismissed

  /** Rail icon stays visible while the side panel is open — not only during the short completion UI window. */
  const tailoringRailIconVisible =
    !optimization.agentPanelDismissed &&
    (tailoringAgentSidePanelOpen ||
      optimization.agentPanelMinimized ||
      optimization.qualityGatePanel !== 'hidden' ||
      Boolean(optimization.qualityEval && optimization.runForAgentUi))

  const editorLocked =
    optimization.showOptimizationAgentUi &&
    (optimizationAgentUi.phase === 'starting' ||
      optimizationAgentUi.phase === 'preparing' ||
      optimizationAgentUi.phase === 'blocks')

  /** Emerald/cyan block rings only while sections are actively optimizing — not in post-run review. */
  const showOptimizationBlockRings = optimizationAgentUi.phase === 'blocks'

  /** Scroll/focus only while blocks are optimizing — not when checklist progress re-filters issues. */
  const optimizationBlockFocusScrollKey = optimizationAgentUi.activeBlock?.block_id ?? ''

  useEffect(() => {
    if (optimizationAgentUi.phase !== 'blocks') return
    const focusSectionTypes = optimizationAgentUi.focusSectionTypes
    if (!focusSectionTypes.length) return
    setExpandedSections((prev) => {
      const next = new Set(prev)
      for (const sectionType of focusSectionTypes) {
        next.add(sectionType)
      }
      return next
    })
    const firstFocus = focusSectionTypes[0]
    if (!firstFocus) return
    const sectionEl = document.querySelector(`[data-section="${firstFocus}"]`)
    if (sectionEl) {
      setTimeout(() => {
        sectionEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 150)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll only when active optimization block changes
  }, [optimizationAgentUi.phase, optimizationBlockFocusScrollKey])

  const handleTemplateChange = (id: ResumeTemplateId) => {
    setResumeTemplate(id)
    saveResumeTemplatePreference(id)
  }

  useEffect(() => {
    const resumeIdForFlush = id
    if (id) {
      loadResume()
    }

    // Flush pending saves for the resume being left (not only the newly active id).
    return () => {
      flushPendingResumeSectionSaves(
        saveTimeoutRef,
        pendingSectionSaveRef,
        getActiveResumeId,
        saveResumeContent,
        { flushForResumeId: resumeIdForFlush },
      )
      Object.values(saveTimeoutRef.current).forEach((timeout) => {
        if (timeout) {
          clearTimeout(timeout)
        }
      })
    }
  }, [id, loadResume, getActiveResumeId, saveResumeContent])

  useEffect(() => {
    const onPageHide = () => {
      flushPendingResumeSectionSaves(
        saveTimeoutRef,
        pendingSectionSaveRef,
        getActiveResumeId,
        saveResumeContent,
      )
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [getActiveResumeId, saveResumeContent])

  useEffect(() => {
    workExperienceSortHydratedRef.current = false
  }, [id])

  useEffect(() => {
    if (!id || loading || workExperienceSortHydratedRef.current) return

    const companies = resumeData.work_experience as WorkExperienceCompany[]
    if (!Array.isArray(companies)) return

    workExperienceSortHydratedRef.current = true
    const stored = loadWorkExperienceSortState(id)

    const groupMode = stored?.groupMode ?? 'company'
    setWorkExperienceGroupMode(groupMode)

    if (stored?.mode === 'by_date') {
      const manual = stored.manualBackup
        ? resolveWorkExperienceManualBackup(companies, stored)
        : cloneWorkExperienceCompanies(companies)
      workExperienceManualBackupRef.current = cloneWorkExperienceCompanies(manual)
      setWorkExperienceSortMode('by_date')
      const sorted = applyWorkExperienceDisplayOrder(manual, {
        sortMode: 'by_date',
        groupMode,
      })
      setResumeData((prev) => ({ ...prev, work_experience: sorted }))
      if (stored.manualBackup && manual !== stored.manualBackup) {
        saveWorkExperienceSortState(id, 'by_date', manual, groupMode)
      }
      return
    }

    setWorkExperienceSortMode('manual')
    const manual = resolveWorkExperienceManualBackup(companies, stored)
    workExperienceManualBackupRef.current = cloneWorkExperienceCompanies(manual)
    const view = applyWorkExperienceDisplayOrder(manual, {
      sortMode: 'manual',
      groupMode,
    })
    if (groupMode !== 'company' || stored?.manualBackup) {
      setResumeData((prev) => ({ ...prev, work_experience: view }))
    }
    saveWorkExperienceSortState(
      id,
      'manual',
      workExperienceManualBackupRef.current,
      groupMode
    )
  }, [id, loading, resumeData.work_experience, setResumeData])

  const persistWorkExperience = useCallback(
    (companies: WorkExperienceCompany[]) => {
      setResumeData((prev) => ({ ...prev, work_experience: companies }))
      scheduleDebouncedResumeSectionSave(
        saveTimeoutRef,
        'work_experience',
        id,
        getActiveResumeId,
        companies,
        saveResumeContent,
        undefined,
        pendingSectionSaveRef,
      )
    },
    [saveResumeContent, setResumeData, id, getActiveResumeId, pendingSectionSaveRef],
  )

  const handleWorkExperienceChange = useCallback(
    (newData: WorkExperienceCompany[]) => {
      let next = sanitizeWorkExperienceCompanies(newData)
      if (workExperienceGroupMode === 'position') {
        next = normalizeWorkExperienceForPositionMode(newData)
      }
      if (workExperienceSortMode === 'by_date') {
        next = applyWorkExperienceDisplayOrder(next, {
          sortMode: 'by_date',
          groupMode: workExperienceGroupMode,
        })
      } else {
        workExperienceManualBackupRef.current = cloneWorkExperienceCompanies(next)
        if (id) {
          saveWorkExperienceSortState(
            id,
            'manual',
            workExperienceManualBackupRef.current,
            workExperienceGroupMode
          )
        }
      }
      persistWorkExperience(next)
    },
    [workExperienceSortMode, workExperienceGroupMode, id, persistWorkExperience]
  )

  const persistWorkExperienceLayout = useCallback(
    (companies: WorkExperienceCompany[]) => {
      if (saveTimeoutRef.current.work_experience) {
        clearTimeout(saveTimeoutRef.current.work_experience)
      }
      setResumeData((prev) => ({ ...prev, work_experience: companies }))
      saveResumeContent('work_experience', companies, true)
    },
    [saveResumeContent, setResumeData]
  )

  const handleWorkExperienceSortModeChange = useCallback(
    (mode: WorkExperienceSortMode) => {
      const current = resumeData.work_experience as WorkExperienceCompany[]
      if (!Array.isArray(current)) return

      if (mode === 'by_date') {
        if (workExperienceSortMode === 'manual') {
          workExperienceManualBackupRef.current = cloneWorkExperienceCompanies(current)
        }
        const sorted = applyWorkExperienceDisplayOrder(current, {
          sortMode: 'by_date',
          groupMode: workExperienceGroupMode,
        })
        setWorkExperienceSortMode('by_date')
        if (id) {
          saveWorkExperienceSortState(
            id,
            'by_date',
            workExperienceManualBackupRef.current,
            workExperienceGroupMode
          )
        }
        persistWorkExperienceLayout(sorted)
        return
      }

      const restored =
        workExperienceManualBackupRef.current ?? cloneWorkExperienceCompanies(current)
      workExperienceManualBackupRef.current = cloneWorkExperienceCompanies(restored)
      setWorkExperienceSortMode('manual')
      if (id) {
        saveWorkExperienceSortState(
          id,
          'manual',
          workExperienceManualBackupRef.current,
          workExperienceGroupMode
        )
      }
      persistWorkExperienceLayout(restored)
    },
    [
      resumeData.work_experience,
      workExperienceSortMode,
      workExperienceGroupMode,
      id,
      persistWorkExperienceLayout,
    ]
  )

  const handleWorkExperienceGroupModeChange = useCallback(
    (groupMode: WorkExperienceGroupMode) => {
      const current = resumeData.work_experience as WorkExperienceCompany[]
      if (!Array.isArray(current)) return

      const next = applyWorkExperienceDisplayOrder(current, {
        sortMode: workExperienceSortMode,
        groupMode,
      })

      setWorkExperienceGroupMode(groupMode)
      workExperienceManualBackupRef.current = cloneWorkExperienceCompanies(next)
      if (id) {
        saveWorkExperienceSortState(
          id,
          workExperienceSortMode,
          workExperienceManualBackupRef.current,
          groupMode
        )
      }
      persistWorkExperienceLayout(next)
    },
    [
      resumeData.work_experience,
      workExperienceSortMode,
      id,
      persistWorkExperienceLayout,
    ]
  )

  // Check if panels have scroll and show/hide scrollbar accordingly
  useEffect(() => {
    const checkScroll = () => {
      requestAnimationFrame(() => {
        if (leftPanelRef.current) {
          const hasScroll = leftPanelRef.current.scrollHeight > leftPanelRef.current.clientHeight
          if (hasScroll) {
            leftPanelRef.current.classList.add(HAS_SCROLL_CLASS)
          } else {
            leftPanelRef.current.classList.remove(HAS_SCROLL_CLASS)
          }
        }
        if (rightPanelRef.current) {
          const hasScroll = rightPanelRef.current.scrollHeight > rightPanelRef.current.clientHeight
          if (hasScroll) {
            rightPanelRef.current.classList.add(HAS_SCROLL_CLASS)
          } else {
            rightPanelRef.current.classList.remove(HAS_SCROLL_CLASS)
          }
        }
      })
    }

    // Check on mount and when content changes
    const timeoutId = setTimeout(checkScroll, 100)
    
    // Use ResizeObserver to detect content changes
    const leftObserver = leftPanelRef.current ? new ResizeObserver(checkScroll) : null
    const rightObserver = rightPanelRef.current ? new ResizeObserver(checkScroll) : null

    if (leftPanelRef.current && leftObserver) {
      leftObserver.observe(leftPanelRef.current)
    }
    if (rightPanelRef.current && rightObserver) {
      rightObserver.observe(rightPanelRef.current)
    }

    // Also check on window resize
    window.addEventListener('resize', checkScroll)

    return () => {
      clearTimeout(timeoutId)
      if (leftObserver) leftObserver.disconnect()
      if (rightObserver) rightObserver.disconnect()
      window.removeEventListener('resize', checkScroll)
    }
  }, [resumeData, expandedSections])


  const toggleSectionWrapper = (sectionType: ResumeSectionType) => {
    toggleSection(sectionType, expandedSections, setExpandedSections)
  }

  const focusEducationEntry = useCallback((educationId: string) => {
    setExpandedSections((prev) => {
      if (prev.has('education')) return prev
      const next = new Set(prev)
      next.add('education')
      return next
    })
    scrollToSelectorInLeftPanel(leftPanelRef, `[data-education-id="${CSS.escape(educationId)}"]`, {
      initialDelayMs: 50,
    })
  }, [])

  const sectionFeedbackRecommendations = useCallback(
    (sectionType: ResumeSectionType): string[] => issuesForSection(activeQualityIssues, sectionType),
    [activeQualityIssues]
  )

  const sectionHasInlineFeedback = useCallback(
    (sectionType: ResumeSectionType): boolean => {
      if (sectionFeedbackRecommendations(sectionType).length > 0) return true
      return (
        sectionType === 'skills' && skillGapHasActionItems(optimizationSkillGapSummary)
      )
    },
    [optimizationSkillGapSummary, sectionFeedbackRecommendations]
  )

  const scrollToSectionFeedback = useCallback(
    (sectionType: ResumeSectionType) => {
      const wasCollapsed = !expandedSections.has(sectionType)
      if (wasCollapsed) {
        setExpandedSections((prev) => new Set(prev).add(sectionType))
      }
      scrollToSelectorInLeftPanel(
        leftPanelRef,
        `[data-section-feedback-stack="${sectionType}"], [data-section-quality-feedback="${sectionType}"]`,
        { initialDelayMs: wasCollapsed ? 350 : 50 }
      )
    },
    [expandedSections]
  )

  const tailoringJobDescription = optimization.activeRun?.job_description?.trim() ?? ''

  const handleRegenerateProfessionalSummary = useCallback(async () => {
    if (!id || summaryRegenerating) return
    const feedback = issuesForSection(activeQualityIssues, 'professional_summary')
    if (!tailoringJobDescription || tailoringJobDescription.length < 80) {
      showToastError('Run tailoring with a job description first, then regenerate the summary.')
      return
    }
    if (!feedback.length) {
      showToastError('No open summary feedback to apply.')
      return
    }
    setSummaryRegenerating(true)
    try {
      await regenerateProfessionalSummary({
        resumeId: id,
        jobDescription: tailoringJobDescription,
        feedback,
        modelStack: optimization.modelStack,
      })
      await loadResume({ silent: true })
      showToastSuccess('Professional summary regenerated from feedback.')
      setExpandedSections((prev) => new Set(prev).add('professional_summary'))
    } catch (err) {
      showToastError(err instanceof Error ? err.message : 'Summary regeneration failed.')
    } finally {
      setSummaryRegenerating(false)
    }
  }, [
    id,
    summaryRegenerating,
    activeQualityIssues,
    tailoringJobDescription,
    optimization.modelStack,
    loadResume,
  ])

  const updateContactInfoWrapper = (field: keyof ContactInfo, value: string | boolean | ContactExtraLink[]) => {
    updateContactInfoHandler(
      field,
      value,
      resumeData,
      setResumeData,
      saveTimeoutRef,
      saveResumeContent,
      id,
      getActiveResumeId,
      pendingSectionSaveRef,
    )
  }

  const handleExportPDF = async () => {
    if (!previewRef.current || !resume || !resumeData) return
    
    try {
      const filename = `${resume.title || 'resume'}.pdf`
      await exportResumeToPDF(previewRef.current, filename, resumeData, resumeTemplate, resumeColors)
      trackResumeExported('pdf', resume.id)
    } catch (error) {
      alert('Failed to export resume to PDF. Please try again.')
    }
  }

  const handleExportDOCX = async () => {
    if (!previewRef.current || !resume) return
    
    try {
      const filename = `${resume.title || 'resume'}.docx`
      await exportResumeToDOCX(previewRef.current, filename, resumeTemplate, resumeColors)
      trackResumeExported('docx', resume.id)
    } catch (error) {
      alert('Failed to export resume to DOCX. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-text-muted">Loading resume…</div>
      </div>
    )
  }

  if (error || !resume) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="rounded-md border border-danger/20 bg-danger-subtle p-4 text-danger">
          {error || 'Resume not found'}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex-1 flex flex-col overflow-hidden min-w-0${previewOnly ? ' bg-surface-subtle' : ' bg-surface-default'}`}
    >
      {!previewOnly && (
      <div
        className={`border-b border-border-default px-6 py-2.5 flex-shrink-0 relative${
          optimizationAgentUi.dimToolbar ? ' resume-opt-ui-dimmed' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              className="w-11 px-0"
              onClick={() => navigate('/resumes')}
              aria-label="Back to Resumes"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Button>
            {editingResumeTitle ? (
              <input
                type="text"
                value={resumeTitleValue}
                onChange={(e) => setResumeTitleValue(e.target.value)}
                onBlur={async () => {
                  if (resumeTitleValue.trim() && id) {
                    const { error } = await supabase
                      .from('resumes')
                      .update({ title: resumeTitleValue.trim() })
                      .eq('id', id)
                    if (!error) {
                      setResume((prev) => prev ? { ...prev, title: resumeTitleValue.trim() } : null)
                    }
                  }
                  setEditingResumeTitle(false)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.currentTarget.blur()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setResumeTitleValue(resume?.title || '')
                    setEditingResumeTitle(false)
                  }
                }}
                className="text-h2 text-text-primary border border-interactive rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            ) : (
              <h1
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                role="button"
                tabIndex={0}
                className="text-h2 text-text-primary cursor-text hover:text-interactive transition-colors min-w-0"
                onClick={() => {
                  setResumeTitleValue(resume?.title || '')
                  setEditingResumeTitle(true)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setResumeTitleValue(resume?.title || '')
                    setEditingResumeTitle(true)
                  }
                }}
              >
                {resume.title}
              </h1>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ResumeOptimizeButton
              onClick={optimization.openJdModal}
              isRunning={optimization.isOptimizing}
              loadingLabel={optimization.isRunFinalizing ? 'Finishing…' : 'Optimizing…'}
            />
            <InstantTooltip
              label={resume.isTemplate ? 'Remove template' : 'Use as template'}
            >
              <Button
                type="button"
                variant="outline"
                outlineTone="toolbar"
                className={`w-11 px-0 ${resume.isTemplate ? 'border-brand-primary/30 bg-brand-primary-subtle/40' : ''}`}
                disabled={togglingTemplate}
                aria-pressed={Boolean(resume.isTemplate)}
                aria-label={resume.isTemplate ? 'Remove template mark' : 'Use as template'}
                data-testid="resume-detail-template-toggle"
                onClick={() => void handleToggleTemplateResume()}
              >
                <TemplateResumeMarkIcon active={Boolean(resume.isTemplate)} className="w-7 h-7" strokeWidth={2.25} />
              </Button>
            </InstantTooltip>
            <Button
              type="button"
              variant="outline"
              outlineTone="toolbar"
              className="gap-1.5"
              onClick={handleExportPDF}
              title="Download as PDF"
              aria-label="Download as PDF"
            >
              <FileFormatPdfIcon className="w-6 h-6 shrink-0" />
              <span>PDF</span>
            </Button>
            <Button
              type="button"
              variant="outline"
              outlineTone="toolbar"
              className="gap-1.5"
              onClick={handleExportDOCX}
              title="Download as Word document"
              aria-label="Download as Word document"
            >
              <FileFormatDocxIcon className="w-6 h-6 shrink-0" />
              <span>Word</span>
            </Button>
          </div>
        </div>

      </div>
      )}

      {/* Main Content - Two Panel Layout */}
      <div
        ref={splitRef}
        className={`resume-detail-split flex-1 flex overflow-hidden min-w-0${
          previewOnly ? ' resume-detail-split--preview-only' : ''
        }${isResizing ? ' resume-detail-split-resizing' : ''}`}
      >
          {!previewOnly && (
          <div
            ref={leftPanelRef}
            className={`resume-detail-editor-panel flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-surface-muted resume-scrollable${
              isResized ? ' resume-detail-editor-panel--resized' : ''
            }${optimizationAgentUi.showPanel ? ' relative' : ''}${
              editorLocked ? ' resume-detail-editor-panel--locked' : ''
            }`}
            style={editorPanelStyle}
            aria-disabled={editorLocked || undefined}
          >
            {editorLocked && (
              <div
                className={`resume-opt-editor-lock absolute inset-0 z-50 cursor-not-allowed${
                  optimizationAgentUi.dimAllSections ? ' resume-opt-editor-scrim' : ''
                }`}
                aria-hidden
                data-testid="optimization-editor-lock-overlay"
              />
            )}
            <div className="p-4 space-y-2">
              {RESUME_SECTIONS.map((section) => {
                const isExpanded = expandedSections.has(section.type)
                const optBlock = resolveSectionOptimizationBlock(section.type)
                const overlayBlock =
                  optBlock &&
                  optimizationAgentUi.activeBlock?.block_id === optBlock.block_id
                    ? optimizationAgentUi.activeBlock
                    : optBlock
                const sectionDimmed = isSectionDimmedForOptimization(section.type, optimizationAgentUi)
                const sectionFocused =
                  showOptimizationBlockRings &&
                  isSectionFocusedForOptimization(section.type, optimizationAgentUi)
                const inlineFeedbackItems = sectionFeedbackRecommendations(section.type)
                const showsSkillsGapPanel =
                  section.type === 'skills' &&
                  skillGapHasActionItems(optimizationSkillGapSummary)
                const showsSectionFeedbackStack =
                  inlineFeedbackItems.length > 0 || showsSkillsGapPanel
                const hasInlineFeedback = sectionHasInlineFeedback(section.type)
                const sectionQualityFlagged =
                  hasInlineFeedback ||
                  isSectionQualityFlagged(section.type, optimizationAgentUi.focusSectionTypes)
                return (
                  <OptimizationBlockOverlay
                    key={section.type}
                    block={overlayBlock}
                    onFailureClick={optimization.setFailureDialogBlock}
                    showBadge={false}
                    showRunningScrim={false}
                    showCompleteRing={showOptimizationBlockRings}
                    className={`${sectionDimmed ? 'resume-opt-section-dimmed' : ''}${
                      sectionFocused ? ' resume-opt-section-focus' : ''
                    }${sectionQualityFlagged ? ' resume-opt-section-quality-flag' : ''}`}
                  >
                  <div
                    className={`rounded-lg border ${
                      sectionQualityFlagged
                        ? 'border-amber-300/60 bg-amber-50'
                        : 'border-border-default bg-surface-default'
                    }`}
                    data-section={section.type}
                  >
                    <div className={`flex items-center justify-between p-3 bg-surface-default border-b border-border-default ${
                      isExpanded ? 'sticky top-0 z-10' : ''
                    }`}>
                      <button
                        onClick={() => toggleSectionWrapper(section.type)}
                        className={`flex flex-1 text-left focus:outline-none min-w-0 ${
                          section.type === 'skills' && isExpanded && activeSkillCategoryName
                            ? 'flex-col items-start gap-0.5'
                            : 'items-center gap-2'
                        }`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                        <motion.svg
                          className="w-4 h-4 text-text-muted shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          animate={{ rotate: isExpanded ? 90 : 0 }}
                          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </motion.svg>
                        <span className="font-medium text-text-primary">{section.label}</span>
                        </span>
                        {section.type === 'skills' && isExpanded && activeSkillCategoryName && (
                          <span
                            className="text-xs font-semibold text-interactive pl-6 truncate max-w-full"
                            data-testid="skills-sticky-category-name"
                          >
                            {activeSkillCategoryName}
                          </span>
                        )}
                      </button>
                      <div className="flex shrink-0 items-center gap-1">
                        {hasInlineFeedback && (
                          <button
                            type="button"
                            data-testid={`section-feedback-indicator-${section.type}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              scrollToSectionFeedback(section.type)
                            }}
                            className="inline-flex items-center rounded p-1 text-amber-600 hover:bg-amber-50 hover:text-amber-800"
                            aria-label={`View tailoring feedback for ${section.label}`}
                            title="View tailoring feedback"
                          >
                            <AlertTriangle className="h-4 w-4" aria-hidden />
                          </button>
                        )}
                        {overlayBlock && overlayBlock.status !== 'pending' && (
                          <OptimizationBlockBadge
                            block={overlayBlock}
                            onFailureClick={optimization.setFailureDialogBlock}
                          />
                        )}
                        {!editorLocked && (
                        <div className="flex items-center gap-0.5">
                          {section.type === 'work_experience' && (
                            <WorkExperienceSectionMenu
                              sortMode={workExperienceSortMode}
                              groupMode={workExperienceGroupMode}
                              onSortModeChange={handleWorkExperienceSortModeChange}
                              onGroupModeChange={handleWorkExperienceGroupModeChange}
                            />
                          )}
                          <button
                            type="button"
                            data-testid={`section-add-${section.type}`}
                            onClick={() => {
                              handleSectionAddClick(
                                section.type,
                                expandedSections,
                                setExpandedSections,
                                addSummaryEntryRef,
                                addWorkExperienceRef,
                                addEducationRef,
                                addTargetTitleRef,
                                addContactExtraFieldRef,
                                addCertificationRef,
                                addProjectRef,
                                addInterestRef,
                                leftPanelRef
                              )
                            }}
                            className="p-1 text-text-muted hover:text-text-primary"
                            title="Добавить"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                        )}
                      </div>
                    </div>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ 
                            duration: 0.25, 
                            ease: [0.4, 0, 0.2, 1] 
                          }}
                          style={{ overflow: 'hidden' }}
                        >
                          <div className="p-3 border-t border-border-default">
                            {showsSectionFeedbackStack && (
                              <div
                                className={SECTION_FEEDBACK_STACK_CLASS}
                                data-testid="section-feedback-stack"
                                data-section-feedback-stack={section.type}
                              >
                                {inlineFeedbackItems.length > 0 && (
                                  <SectionQualityFeedback
                                    sectionType={section.type}
                                    recommendations={inlineFeedbackItems}
                                    showRegenerateButton={
                                      section.type === 'professional_summary' &&
                                      inlineFeedbackItems.length > 0 &&
                                      tailoringJobDescription.length >= 80 &&
                                      !editorLocked
                                    }
                                    regenerating={summaryRegenerating}
                                    onRegenerateWithFeedback={
                                      section.type === 'professional_summary'
                                        ? () => void handleRegenerateProfessionalSummary()
                                        : undefined
                                    }
                                  />
                                )}
                                {showsSkillsGapPanel && (
                                  <OptimizationSkillsGapRecommendations
                                    run={optimization.activeRun}
                                  />
                                )}
                              </div>
                            )}
                            <ResumeSectionEditor
                              sectionType={section.type}
                              resumeId={id}
                              getActiveResumeId={getActiveResumeId}
                              data={resumeData[section.type]}
                              resumeData={resumeData}
                              saveTimeoutRef={saveTimeoutRef}
                              pendingSectionSaveRef={pendingSectionSaveRef}
                              onUpdateResumeData={setResumeData}
                              onSaveResumeContent={saveResumeContent}
                              onUpdateContactInfo={updateContactInfoWrapper}
                              addSummaryEntryRef={addSummaryEntryRef}
                              addWorkExperienceRef={addWorkExperienceRef}
                              addEducationRef={addEducationRef}
                              addRoleRef={addRoleRef}
                              addTargetTitleRef={addTargetTitleRef}
                              addContactExtraFieldRef={addContactExtraFieldRef}
                              addCertificationRef={addCertificationRef}
                              addProjectRef={addProjectRef}
                              addInterestRef={addInterestRef}
                              highlightedRoleId={highlightedRoleId}
                              highlightedBulletId={highlightedBulletId}
                              highlightedCompanyId={highlightedCompanyId}
                              highlightedTargetTitleId={highlightedTargetTitleId}
                              highlightedSummaryId={highlightedSummaryId}
                              onWorkExperienceChange={handleWorkExperienceChange}
                              skillsScrollRootRef={leftPanelRef}
                              onActiveSkillCategoryChange={setActiveSkillCategoryName}
                              accountSummaryVariants={accountSummaryVariants}
                              accountSummaryVariantsLoading={accountSummaryVariantsLoading}
                              accountTargetTitleVariants={accountTargetTitleVariants}
                              accountTargetTitleVariantsLoading={accountTargetTitleVariantsLoading}
                              onFocusEducationEntry={focusEducationEntry}
                              isTemplateResume={Boolean(resume?.isTemplate)}
                              templateResumes={templateResumes}
                              fetchTemplateSkillsForResume={fetchTemplateSkillsForResume}
                            />
                            {section.type === 'contact_info' && (
                              <OptimizationContactRecommendations
                                block={optBlock}
                                progressContext={feedbackProgressCtx}
                              />
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  </OptimizationBlockOverlay>
                )
              })}
            </div>
          </div>
          )}

          {!previewOnly && (
            <ResumeDetailSplitResizeHandle
              onPointerDown={onResizePointerDown}
              isResizing={isResizing}
            />
          )}

          {/* Right Panel — flex; A4 sheet zooms to fit with symmetric padding */}
          <div className="resume-detail-preview-panel flex min-h-0 overflow-hidden bg-surface-subtle">
            <div
              ref={rightPanelRef}
              className={`min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden resume-scrollable${
                optimizationAgentUi.dimPreview ? ' relative' : ''
              }`}
              style={{
                boxSizing: 'border-box',
                padding: effectivePreviewPaddingPx,
              }}
            >
            {optimizationAgentUi.dimPreview && (
              <div
                className="resume-opt-preview-scrim pointer-events-none absolute inset-0 z-30"
                aria-hidden
                data-testid="optimization-preview-scrim"
              />
            )}
            <div className="mx-auto flex w-full max-w-[1100px] justify-center">
              <div ref={previewRef} className="relative min-w-0 flex-1">
                <ResumePreviewPageGuides
                  template={resumeTemplate}
                  lineGuides={lineGuides}
                  fontSizeDebug={fontSizeDebug}
                >
                <ResumePreview 
                data={resumeData}
                template={resumeTemplate}
                colors={resumeColors}
                onTargetTitleClick={() => {
                const wasCollapsed = !expandedSections.has('target_title')
                if (wasCollapsed) {
                  toggleSectionWrapper('target_title')
                }
                scrollToSelectorInLeftPanel(leftPanelRef, '[data-section="target_title"]', {
                  initialDelayMs: wasCollapsed ? 350 : 50,
                })
              }}
              onTargetTitleItemClick={(title) => {
                const wasCollapsed = !expandedSections.has('target_title')
                if (wasCollapsed) {
                  toggleSectionWrapper('target_title')
                }
                setHighlightedTargetTitleId(title)
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-title-id="${CSS.escape(title)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 },
                )
                // Remove highlight after animation
                setTimeout(() => {
                  setHighlightedTargetTitleId(null)
                }, 2000)
              }}
              onWorkExperienceRoleClick={(roleId) => {
                const wasCollapsed = !expandedSections.has('work_experience')
                if (wasCollapsed) {
                  toggleSectionWrapper('work_experience')
                }
                setHighlightedCompanyId(null)
                setHighlightedRoleId(roleId)
                setHighlightedBulletId(null)
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-role-id="${CSS.escape(roleId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 }
                )
                setTimeout(() => {
                  setHighlightedRoleId(null)
                }, 2000)
              }}
              onWorkExperienceCompanyClick={(companyId) => {
                const wasCollapsed = !expandedSections.has('work_experience')
                if (wasCollapsed) {
                  toggleSectionWrapper('work_experience')
                }
                setHighlightedCompanyId(companyId)
                setHighlightedRoleId(null)
                setHighlightedBulletId(null)
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-company-id="${CSS.escape(companyId)}"] [data-wx-company-block="true"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 }
                )
                setTimeout(() => {
                  setHighlightedCompanyId(null)
                }, 2000)
              }}
              onWorkExperienceBulletClick={(roleId, bulletId) => {
                const wasCollapsed = !expandedSections.has('work_experience')
                if (wasCollapsed) {
                  toggleSectionWrapper('work_experience')
                }
                setHighlightedCompanyId(null)
                setHighlightedRoleId(null)
                setHighlightedBulletId(bulletId)
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-bullet-id="${CSS.escape(bulletId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 }
                )
                setTimeout(() => {
                  setHighlightedBulletId(null)
                }, 2000)
              }}
              onProfessionalSummaryClick={(summaryId) => {
                const wasCollapsed = !expandedSections.has('professional_summary')
                if (wasCollapsed) {
                  toggleSectionWrapper('professional_summary')
                }
                setHighlightedSummaryId(summaryId)
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-summary-id="${CSS.escape(summaryId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 },
                )
                // Remove highlight after animation
                setTimeout(() => {
                  setHighlightedSummaryId(null)
                }, 2000)
              }}
              onEducationClick={(educationId) => {
                const wasCollapsed = !expandedSections.has('education')
                if (wasCollapsed) {
                  toggleSectionWrapper('education')
                }
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-education-id="${CSS.escape(educationId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 },
                )
              }}
              onSkillCategoryClick={(categoryId) => {
                const wasCollapsed = !expandedSections.has('skills')
                if (wasCollapsed) {
                  toggleSectionWrapper('skills')
                }
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-skill-category-id="${CSS.escape(categoryId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 },
                )
              }}
              onCertificationClick={(certId) => {
                const wasCollapsed = !expandedSections.has('certifications')
                if (wasCollapsed) {
                  toggleSectionWrapper('certifications')
                }
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-certification-id="${CSS.escape(certId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 },
                )
              }}
                onProjectClick={(projectId) => {
                const wasCollapsed = !expandedSections.has('projects')
                if (wasCollapsed) {
                  toggleSectionWrapper('projects')
                }
                scrollToSelectorInLeftPanel(
                  leftPanelRef,
                  `[data-project-id="${CSS.escape(projectId)}"]`,
                  { initialDelayMs: wasCollapsed ? 350 : 50 },
                )
                }}
              />
                </ResumePreviewPageGuides>
              </div>
            </div>
            </div>
            {optimizationAgentUi.showPanel &&
              !optimization.agentPanelMinimized &&
              !optimization.agentPanelDismissed && (
            <OptimizationAgentPanel
              agentUi={optimizationAgentUi}
              runId={optimization.activeRun?.id ?? optimization.runForAgentUi?.id}
              qualityEval={optimization.qualityEval}
              qualityIssues={activeQualityIssues}
              skillGapSummary={optimizationSkillGapSummary}
              liveSummaryProgrammaticPass={summaryPassesWritingChecks(
                resumeData.professional_summary ?? []
              )}
              onMinimize={optimization.minimizeAgentPanel}
            />
            )}
            <ResumePreviewSettingsRail
              template={resumeTemplate}
              onTemplateChange={handleTemplateChange}
              colorPreference={resumeColorPreference}
              onColorPreferenceChange={handleResumeColorPreferenceChange}
              agentSlot={
                tailoringRailIconVisible
                  ? {
                      headline: optimizationAgentUi.headline || 'Tailoring results',
                      headerStatus: optimizationAgentUi.headerStatus,
                      onExpand: optimization.expandAgentPanel,
                      onCollapse: optimization.minimizeAgentPanel,
                      panelOpen: tailoringAgentSidePanelOpen,
                      qualityEval: optimization.qualityEval,
                      qualityIssues: activeQualityIssues,
                    }
                  : null
              }
            />
            {optimization.qualityEval &&
              optimization.activeRun &&
              !optimization.showOptimizationAgentUi && (
              <OptimizationQualityGate
                panelState={optimization.qualityGatePanel}
                runId={optimization.activeRun.id}
                qualityEval={optimization.qualityEval}
                qualityIssues={activeQualityIssues}
                liveSummaryProgrammaticPass={summaryPassesWritingChecks(
                  resumeData.professional_summary ?? []
                )}
                skillGapSummary={optimizationSkillGapSummary}
                onMinimize={optimization.minimizeQualityGate}
                onExpand={optimization.expandQualityGate}
                onDismiss={optimization.dismissQualityGate}
              />
            )}
          </div>
        </div>

      <JobDescriptionModal
        isOpen={optimization.jdModalOpen}
        value={jdDraft}
        modelStack={optimization.modelStack}
        eligibilityWarnings={eligibilityWarnings}
        eligibilityLoading={eligibilityLoading}
        eligibilityReady={eligibilityReady}
        jdPrecheckLoading={jdPrecheckLoading}
        jdSecurityBlocked={jdSecurityBlocked}
        jdSecurityMessage={jdSecurityMessage}
        jdSecurityMediumMessage={jdSecurityMediumMessage}
        jdPrecheckNetworkWarning={jdPrecheckNetworkWarning}
        onChange={setJdDraft}
        onModelStackChange={optimization.setModelStack}
        onSubmit={() => void optimization.startOptimization(jdDraft, optimization.modelStack)}
        onCancel={optimization.closeJdModal}
        submitting={optimization.starting}
        error={optimization.startError}
      />
      <OptimizationFailureDialog
        block={optimization.failureDialogBlock}
        onClose={() => optimization.setFailureDialogBlock(null)}
      />
      <QuotaExhaustedModal
        isOpen={optimization.quotaModalOpen}
        tier={optimization.quotaModalTier}
        used={optimization.quotaModalUsed}
        cap={optimization.quotaModalCap}
        reason={optimization.quotaModalReason}
        duplicateLayer={optimization.quotaModalDuplicateLayer}
        onClose={optimization.closeQuotaModal}
      />
    </div>
  )
}

export default ResumeDetail